/**
 * The ADMIN's org for a shared user (KAN-178).
 *
 * Shared/org-level docs (team permissions today) live under
 * `uaAppPreferences/{saas}_orgs/{orgId}/{ext}/shared/{adminUid}`. The admin
 * writes them under *their* org, and every backend reader resolves that same
 * org — but the browser only knows the signed-in user's OWN org. A shared user
 * in another CRM org, on another SaaS, or with no SaaS at all therefore read a
 * different (empty) doc and every permission silently fell back to "allow".
 *
 * Resolution order (first hit wins, result cached per {uid, appCode}):
 *   1. the invite doc's `saasOrgID` — written by newer admin builds, already
 *      loaded with `invitedAdminList`, so no round-trip;
 *   2. localStorage from a previous visit;
 *   3. the `GET_SHARED_ADMIN_ORG` backend action — reads the admin's extension
 *      doc (`org_id`), the source the server itself uses. Covers every
 *      existing invite, however old.
 *
 * If all three fail the path helpers fall back to the user's own org, which
 * is exactly today's behaviour — nothing regresses.
 */

import { create } from 'zustand';
import { useAuthStore } from './auth-store';
import { useAppStore } from './app-store';
import { httpsCallable } from '@/lib/firebase/functions';
import { functions } from '@/config/firebase';
import { readScopedCache, writeScopedCache } from '@/lib/cache/scoped-cache';

export interface SharedAdminOrg {
  adminUid: string;
  /** The app code the invite was found under (admin's extension). */
  appCode: string;
  orgId: string;
  saasCode: string;
}

interface SharedAdminOrgStore {
  org: SharedAdminOrg | null;
  /** `${adminUid}:${appCode}` whose lookup failed — path helpers then fall back. */
  failedKey: string | null;
}

export const useSharedAdminOrgStore = create<SharedAdminOrgStore>(() => ({
  org: null,
  failedKey: null,
}));

const CACHE_BASE = 'ua_cache_sharedAdminOrg';
let pendingKey: string | null = null;

function keyOf(adminUid: string, appCode: string): string {
  return `${adminUid}:${appCode}`;
}

function saasCodeOf(appCode: string): string {
  return appCode.split('for')[1] || '';
}

function readCache(adminUid: string, appCode: string): SharedAdminOrg | null {
  try {
    const raw = readScopedCache(CACHE_BASE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SharedAdminOrg>;
    if (parsed.adminUid === adminUid && parsed.appCode === appCode && parsed.orgId && parsed.saasCode) {
      return parsed as SharedAdminOrg;
    }
  } catch { /* corrupt cache */ }
  return null;
}

function commit(org: SharedAdminOrg, source: string): void {
  useSharedAdminOrgStore.setState({ org, failedKey: null });
  try { writeScopedCache(CACHE_BASE, JSON.stringify(org)); } catch { /* */ }
  console.log(`[SharedAdminOrg] resolved via ${source}:`, org.orgId, `(${org.saasCode}, ${org.appCode})`);
}

/** Resolve (once per admin+app) the admin's org for the current shared user. */
export async function resolveSharedAdminOrg(): Promise<void> {
  const { selectedAdminUserId: adminUid, invitedAdminList, user } = useAuthStore.getState();
  const extensionName = useAppStore.getState().extensionName;
  if (!adminUid || !user || !extensionName) {
    if (useSharedAdminOrgStore.getState().org) useSharedAdminOrgStore.setState({ org: null, failedKey: null });
    return;
  }
  if (invitedAdminList === null) {
    // The invite list is lazy-loaded by the header, which some views (the
    // CRM call bar, for one) never mount. Kick it here; the auth-store
    // subscription re-runs this resolver when it lands, and source 1 then
    // wins without any backend round-trip. Deduped inside the store.
    void useAuthStore.getState().fetchInvitedAdminList().catch(() => {});
  }
  const entry = invitedAdminList?.[adminUid];
  const appCode = entry?.sourceAppCode || extensionName;
  const key = keyOf(adminUid, appCode);
  const { org, failedKey } = useSharedAdminOrgStore.getState();
  if (org && keyOf(org.adminUid, org.appCode) === key) return;

  // The local sources are checked on EVERY run, even after a failed backend
  // lookup: the first run usually fires before the invite list has loaded
  // (the selected admin is restored from localStorage at boot), so the
  // invite doc only becomes available on a later run.
  // 1. Invite doc already carries the admin's org (newer admin builds).
  if (entry?.saasOrgID) {
    commit({ adminUid, appCode, orgId: entry.saasOrgID, saasCode: saasCodeOf(appCode) }, 'invite');
    return;
  }
  // 2. A previous visit resolved it.
  const cached = readCache(adminUid, appCode);
  if (cached) {
    useSharedAdminOrgStore.setState({ org: cached, failedKey: null });
    return;
  }
  // 3. Ask the backend — it reads the admin's extension doc, which the
  //    browser cannot (ulgebraUsers/** is owner-only). Once per admin+app.
  if (failedKey === key || pendingKey === key) return;
  pendingKey = key;
  try {
    const call = httpsCallable<Record<string, unknown>, { status?: string; orgId?: string; saasCode?: string; error?: string }>(
      functions,
      'new_callServiceAuthorizedAPI',
    );
    const resp = await call({
      appCode: 'sharedadmin',
      action: 'GET_SHARED_ADMIN_ORG',
      adminUid,
      adminAppCode: appCode,
    });
    const d = resp.data;
    if (d?.status === 'success' && d.orgId) {
      commit({ adminUid, appCode, orgId: String(d.orgId), saasCode: d.saasCode || saasCodeOf(appCode) }, 'backend');
    } else {
      console.warn('[SharedAdminOrg] backend lookup failed:', d?.error || d);
      useSharedAdminOrgStore.setState({ failedKey: key });
    }
  } catch (err) {
    console.warn('[SharedAdminOrg] backend lookup threw:', err);
    useSharedAdminOrgStore.setState({ failedKey: key });
  } finally {
    if (pendingKey === key) pendingKey = null;
  }
}

/**
 * The org context shared/org-level docs must be read and written under.
 *   admin        → own org (unchanged)
 *   shared user  → the admin's org once resolved; `null` while resolving
 *                  (callers wait, same as an unresolved own org); own org
 *                  when resolution failed (today's behaviour).
 */
export function getSharedOrgContext(): { orgId: string; saasCode: string } | null {
  const appStore = useAppStore.getState();
  const own = appStore.appConfig?.orgId && appStore.appCodeSAAS
    ? { orgId: String(appStore.appConfig.orgId), saasCode: appStore.appCodeSAAS }
    : null;
  const { selectedAdminUserId: adminUid, invitedAdminList } = useAuthStore.getState();
  if (!adminUid) return own;

  const appCode = invitedAdminList?.[adminUid]?.sourceAppCode || appStore.extensionName || '';
  const key = keyOf(adminUid, appCode);
  const { org, failedKey } = useSharedAdminOrgStore.getState();
  if (org && keyOf(org.adminUid, org.appCode) === key) return { orgId: org.orgId, saasCode: org.saasCode };
  if (failedKey === key) return own;
  // Kick resolution OFF the current call stack. Resolving synchronously here
  // (the invite path is sync) would commit → notify this store's subscribers
  // → re-enter team-store's subscribeTeams while the outer call is still
  // running with a null path, and the outer call would then tear down the
  // subscription the inner one just made.
  queueMicrotask(() => { void resolveSharedAdminOrg(); });
  return null;
}

// Re-resolve whenever the selected admin, the invite list, or the app changes.
useAuthStore.subscribe((s, prev) => {
  if (s.selectedAdminUserId !== prev.selectedAdminUserId
    || s.invitedAdminList !== prev.invitedAdminList
    || s.user?.uid !== prev.user?.uid) {
    void resolveSharedAdminOrg();
  }
});
useAppStore.subscribe((s, prev) => {
  if (s.extensionName !== prev.extensionName) void resolveSharedAdminOrg();
});
